import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';

import { ActivitiesContentType } from 'global/constants';
import { useGetActivitySections } from 'approot/activity-builder/activity-builder-section.hooks';
import TeachingNotesDetailRichtextEdit from './teaching-notes-detail-richtext-edit';
import { useBatchTeachingNotesContext } from './batch-teaching-notes-update.context';
import { ComponentActivity } from '../component.constants';
import type { GetLessonTeachingAndLearningSequenceQuery } from './teaching-notes.graphql.generated';
import { getActivityPartsSequenceItems } from 'approot/shared/teaching-learning-sequence/logic/get-activity-parts-sequence-items';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  sectionId: string;
  contentId: number;
  contentType: ActivitiesContentType;
  classId: number;
  component: ComponentActivity;
  sequences: Pluck<
    GetLessonTeachingAndLearningSequenceQuery,
    'getLessonTeachingAndLearningSequence'
  >[];
};

const TeachingNotesActivityPartsEdit = ({
  sectionId,
  contentId,
  contentType,
  classId,
  component,
  sequences,
}: Props) => {
  const { data: sectionData } = useGetActivitySections({
    contentId,
    contentType,
    classId,
    sectionIds: [sectionId],
  });

  const { handleUpdate } = useBatchTeachingNotesContext();

  const parts = useMemo(
    () => getActivityPartsSequenceItems(sequences, component?.id),
    [sequences, component]
  );

  const subSections = useMemo(
    () => sortBy(sectionData?.getActivitiesSections?.[0].subSections, 'sort'),
    [sectionData?.getActivitiesSections]
  );

  const sectionDescription = useMemo(
    () => parts.find(a => a.sectionId === sectionId),
    [parts, sectionId]
  );

  const total = useMemo(
    () => (subSections ? subSections.length + 1 : 0),
    [subSections]
  );

  return (
    <>
      <TeachingNotesDetailRichtextEdit
        key={sectionId}
        id={sectionId}
        title={total > 1 ? `Part 1 of ${total}` : 'Teaching Notes'}
        onChange={json => {
          if (sectionDescription) {
            handleUpdate(
              {
                ...sectionDescription,
                data: json,
              },
              sequences
            );
          }
        }}
        data={sectionDescription?.data}
      />
      {subSections?.map((subSection, index) => {
        const subSectionPart = parts.find(d => d.sectionId === subSection.id);
        return (
          <TeachingNotesDetailRichtextEdit
            key={subSection.id}
            id={subSection.id}
            title={`Part ${index + 2} of ${total}`}
            onChange={json => {
              if (subSectionPart) {
                handleUpdate(
                  {
                    ...subSectionPart,
                    data: json,
                  },
                  sequences
                );
              }
            }}
            data={subSectionPart?.data}
          />
        );
      })}
    </>
  );
};

export default TeachingNotesActivityPartsEdit;
